import Header from './components/Header';
import Hero from './components/Hero';
import Footer from './components/Footer';
import {
  topicsData,
  aboutData,
  cardsData,
  appData,
  indexData,
  faqData,
  commentsData,
  rankingData,
  carouselData,
  mediaData,
  updatesData,
  contactData,
  featuresData,
} from '@/mocks/homeData';

function SectionTitle({ id, title, subtitle }: { id: string; title: string; subtitle?: string }) {
  return (
    <div id={id} className="text-center mb-10 md:mb-14 pt-2">
      <h2
        className="text-2xl md:text-3xl font-bold text-foreground-950"
        style={{ fontFamily: 'var(--font-heading)' }}
      >
        {title}
      </h2>
      {subtitle && (
        <p className="mt-3 text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">{subtitle}</p>
      )}
    </div>
  );
}

interface GradientCardProps {
  children: React.ReactNode;
  className?: string;
}

function GradientCard({ children, className = '' }: GradientCardProps) {
  return (
    <div className={`bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden transition-all duration-300 hover:border-primary-200/50 ${className}`}>
      {children}
    </div>
  );
}

export default function Home() {
  return (
    <>
      <Header />
      <main>
        <Hero />

        {/* #topics - 热门话题 */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="px-4 md:px-6 max-w-[1440px] mx-auto">
            <SectionTitle id="topics" title="热门话题" subtitle="大家都在讨论的影视热点，紧跟追剧潮流不落伍" />
            <div className="flex flex-wrap justify-center gap-2.5 md:gap-3">
              {topicsData.map((topic) => (
                <a
                  key={topic.id}
                  href="#cards"
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-background-50 border border-background-200 rounded-full text-sm text-foreground-700 hover:text-primary-500 hover:border-primary-300 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className={`${topic.icon} text-primary-400 text-xs`}></i>
                  <span>{topic.tag}</span>
                  <span className="text-xs text-foreground-400 ml-0.5">{topic.heat}</span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* #about - 关于菲菲影院 */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6 max-w-[1440px] mx-auto">
            <div id="about" className="max-w-3xl mx-auto text-center">
              <h2
                className="text-2xl md:text-3xl font-bold text-foreground-950 mb-5"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                {aboutData.title}
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-7">{aboutData.description}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {aboutData.highlights.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 justify-center px-4 py-3 bg-background-100 rounded-xl">
                    <i className="ri-check-fill text-accent-500 text-sm"></i>
                    <span className="text-sm font-medium text-foreground-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* #cards - 精选推荐 */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6 max-w-[1440px] mx-auto">
            <SectionTitle id="cards" title="精选推荐" subtitle="编辑团队为你精心挑选，每部都值得花时间细细品味" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-5">
              {cardsData.map((card) => (
                <GradientCard key={card.id}>
                  <div className="relative aspect-[16/10] overflow-hidden bg-background-200">
                    <img
                      src={card.image}
                      alt={`菲菲影院${card.title}`}
                      title={`菲菲影院${card.title}`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
                      loading="lazy"
                    />
                    <span className="absolute top-3 left-3 px-2.5 py-0.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                      {card.tag}
                    </span>
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1.5">{card.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{card.desc}</p>
                  </div>
                </GradientCard>
              ))}
            </div>
          </div>
        </section>

        {/* #app - 手机追剧 */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6 max-w-[1440px] mx-auto">
            <div id="app" className="flex flex-col lg:flex-row items-center gap-8 md:gap-12 max-w-4xl mx-auto">
              <div className="flex-1 w-full text-center lg:text-left">
                <h2
                  className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  {appData.title}
                </h2>
                <p className="text-sm md:text-base text-foreground-500 mb-6">{appData.subtitle}</p>
                <div className="space-y-3">
                  {appData.features.map((feat, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 shrink-0">
                        <i className="ri-check-line text-accent-500 text-sm"></i>
                      </div>
                      <span className="text-sm text-foreground-700">{feat}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex items-center justify-center lg:justify-start gap-3">
                  <a
                    href="#hero"
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-foreground-900 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-apple-line"></i>
                    App Store
                  </a>
                  <a
                    href="#hero"
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-foreground-900 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-android-line"></i>
                    安卓下载
                  </a>
                </div>
              </div>
              <div className="flex-1 flex justify-center">
                <div className="relative w-56 h-96 rounded-3xl overflow-hidden bg-background-200 border-4 border-background-300">
                  <div className="absolute inset-0 bg-gradient-to-b from-primary-500/20 to-accent-500/20 flex flex-col items-center justify-center p-6 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-primary-500 mb-4">
                      <i className="ri-film-line text-background-50 text-2xl"></i>
                    </div>
                    <span className="text-lg font-bold text-foreground-900 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>菲菲影院</span>
                    <span className="text-xs text-foreground-500">随时随地 · 高清追剧</span>
                    <div className="mt-4 w-32 h-32 rounded-xl bg-background-50/80 flex items-center justify-center">
                      <i className="ri-qr-code-line text-6xl text-foreground-700"></i>
                    </div>
                    <span className="text-xs text-foreground-500 mt-3">扫码下载</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* #index - 专题合集 */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6 max-w-[1440px] mx-auto">
            <SectionTitle id="index" title="专题合集" subtitle="按你的喜好精准找到好内容，每一个专题都是一次发现之旅" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {indexData.map((item) => (
                <a
                  key={item.id}
                  href="#cards"
                  className="flex items-center gap-4 p-4 md:p-5 bg-background-50 border border-background-200 rounded-xl hover:border-primary-300 transition-all group cursor-pointer"
                >
                  <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${item.color} shrink-0`}>
                    <i className={`${item.icon} text-background-50 text-lg`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground-800 group-hover:text-primary-500 transition-colors truncate">
                      {item.title}
                    </h3>
                    <span className="text-xs text-foreground-500">{item.count}</span>
                  </div>
                  <i className="ri-arrow-right-s-line text-foreground-400 group-hover:text-primary-500 transition-colors text-lg shrink-0"></i>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* #faq - 常见问题 */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6 max-w-[1440px] mx-auto">
            <SectionTitle id="faq" title="常见问题" subtitle="关于菲菲影院的使用疑问，这里都能找到答案" />
            <div className="max-w-3xl mx-auto grid grid-cols-1 gap-3">
              {faqData.map((item) => (
                <details key={item.id} className="group bg-background-100 rounded-xl border border-background-200 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <h3 className="text-sm font-semibold text-foreground-800 pr-4">{item.q}</h3>
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-background-200 group-open:bg-primary-100 shrink-0 transition-colors">
                      <i className="ri-arrow-down-s-line text-foreground-600 group-open:text-primary-500 group-open:rotate-180 transition-all text-sm"></i>
                    </div>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* #comments - 用户评价 */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6 max-w-[1440px] mx-auto">
            <SectionTitle id="comments" title="用户评价" subtitle="听听影迷们怎么说，真实用户的使用感受最有说服力" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-5">
              {commentsData.map((comment) => (
                <GradientCard key={comment.id}>
                  <div className="p-4 md:p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 shrink-0">
                        <img
                          src={comment.avatar}
                          alt={`${comment.name}的头像`}
                          title={comment.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-800">{comment.name}</h3>
                        <span className="text-xs text-foreground-500">{comment.time}</span>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{comment.text}</p>
                    <div className="flex items-center gap-0.5 mt-3">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                      ))}
                    </div>
                  </div>
                </GradientCard>
              ))}
            </div>
          </div>
        </section>

        {/* #ranking - 热播榜单 */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6 max-w-[1440px] mx-auto">
            <SectionTitle id="ranking" title="热播榜单" subtitle="实时热度排行，看看大家都在追什么好剧" />
            <div className="max-w-3xl mx-auto">
              {rankingData.map((item) => (
                <a
                  key={item.id}
                  href="#cards"
                  className="flex items-center gap-4 p-4 hover:bg-background-100 rounded-xl transition-colors group cursor-pointer"
                >
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 ${
                    item.rank <= 3 ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-600'
                  }`}>
                    <span className="text-lg font-bold">{item.rank}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground-800 group-hover:text-primary-500 transition-colors">
                      {item.title}
                    </h3>
                    <span className="text-xs text-foreground-500">{item.type}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${
                      item.hot === 'TOP1' ? 'bg-primary-100 text-primary-700' :
                      item.hot === 'NEW' ? 'bg-accent-100 text-accent-700' :
                      'bg-secondary-100 text-secondary-700'
                    }`}>{item.hot}</span>
                    <span className="text-xs font-semibold text-accent-500">
                      <i className={`${item.trend === 'up' ? 'ri-arrow-up-line' : item.trend === 'new' ? 'ri-sparkling-line' : 'ri-subtract-line'}`}></i>
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* #carousel - 焦点专题 */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6 max-w-[1440px] mx-auto">
            <SectionTitle id="carousel" title="焦点专题" subtitle="深度策划的影视专题，发现更多你不知道的好内容" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              {carouselData.map((item) => (
                <GradientCard key={item.id}>
                  <div className="relative aspect-[16/9] overflow-hidden bg-background-200">
                    <img
                      src={item.image}
                      alt={`菲菲影院${item.title}`}
                      title={`菲菲影院${item.title}`}
                      className="w-full h-full object-cover object-top transition-transform duration-700 hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground-900/60 via-transparent to-transparent"></div>
                    <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-medium">
                      {item.tag}
                    </span>
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <h3 className="text-base md:text-lg font-bold text-background-50 mb-1">{item.title}</h3>
                      <p className="text-sm text-background-200/90 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </GradientCard>
              ))}
            </div>
          </div>
        </section>

        {/* #media - 精彩预告 */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6 max-w-[1440px] mx-auto">
            <SectionTitle id="media" title="精彩预告" subtitle="最新剧集预告片抢先看，好内容值得期待" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {mediaData.map((item) => (
                <GradientCard key={item.id}>
                  <div className="relative aspect-video overflow-hidden bg-background-200 group cursor-pointer">
                    <img
                      src={item.image}
                      alt={`菲菲影院${item.title}`}
                      title={`菲菲影院${item.title}`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50 transition-transform group-hover:scale-110">
                        <i className="ri-play-fill text-xl ml-0.5"></i>
                      </div>
                    </div>
                    <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-foreground-900/70 text-background-50 text-xs font-medium">
                      {item.duration}
                    </span>
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-semibold text-foreground-800 line-clamp-1 mb-1">{item.title}</h3>
                    <div className="flex items-center gap-2 text-xs text-foreground-500">
                      <i className="ri-eye-line"></i>
                      <span>{item.views}次观看</span>
                    </div>
                  </div>
                </GradientCard>
              ))}
            </div>
          </div>
        </section>

        {/* #updates - 最新动态 */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6 max-w-[1440px] mx-auto">
            <SectionTitle id="updates" title="最新动态" subtitle="平台内容实时更新，不错过每一部好作品" />
            <div className="max-w-3xl mx-auto">
              {updatesData.map((item, i) => (
                <div
                  key={item.id}
                  className="flex items-start gap-4 pb-5 relative"
                >
                  {i < updatesData.length - 1 && (
                    <div className="absolute left-[19px] top-12 bottom-0 w-px bg-background-300"></div>
                  )}
                  <div className="w-[38px] h-[38px] flex items-center justify-center rounded-full bg-background-50 border-2 border-primary-500 shrink-0 z-10">
                    <i className="ri-calendar-check-line text-primary-500 text-sm"></i>
                  </div>
                  <div className="flex-1 pt-1.5">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs text-foreground-500">{item.date}</span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${
                        item.type === '新片' ? 'bg-primary-100 text-primary-700' :
                        item.type === '完结' ? 'bg-accent-100 text-accent-700' :
                        item.type === '公告' ? 'bg-secondary-100 text-secondary-700' :
                        item.type === '活动' ? 'bg-primary-100 text-primary-700' :
                        item.type === '里程碑' ? 'bg-accent-100 text-accent-700' :
                        'bg-secondary-100 text-secondary-700'
                      }`}>{item.type}</span>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-800">{item.title}</h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* #contact - 联系我们 */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6 max-w-[1440px] mx-auto">
            <div id="contact" className="max-w-2xl mx-auto text-center">
              <h2
                className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                {contactData.title}
              </h2>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-8">{contactData.description}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-5 bg-background-100 rounded-2xl border border-background-200">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 mx-auto mb-3">
                    <i className="ri-mail-line text-primary-500 text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-800 mb-1">电子邮件</h3>
                  <span className="text-sm text-foreground-500 break-all">{contactData.email}</span>
                </div>
                <div className="p-5 bg-background-100 rounded-2xl border border-background-200">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 mx-auto mb-3">
                    <i className="ri-wechat-line text-accent-500 text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-800 mb-1">微信公众号</h3>
                  <span className="text-sm text-foreground-500">{contactData.wechat}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* #features - 平台优势 */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6 max-w-[1440px] mx-auto">
            <SectionTitle id="features" title="平台优势" subtitle="用心做好每一个细节，只为给你最好的观影体验" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-5">
              {featuresData.map((feature) => (
                <GradientCard key={feature.id}>
                  <div className="p-5 md:p-6 text-center">
                    <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-100 mx-auto mb-4">
                      <i className={`${feature.icon} text-primary-500 text-2xl`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{feature.desc}</p>
                  </div>
                </GradientCard>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}